import { GeneralConfigDocumentData } from "../../../prismicio-types"
import { AdditionalInformationListModel, ArticleModel, ProductImageModel } from "../../api-clients/product-api-v1"
import { sectionColumnBreakpoints } from "../constants/pdp-constants"
import { campaignTypes } from "../constants/search-constants"
import { PriceConfigType } from "../types/prices"
import { getCampaignItems, getSortedItems } from "./search-utils"

export function sortImages(images: ProductImageModel[], isArticle = false) {
  //image orientations
  const PRIMARY_IMAGE_JPG = "PrimaryImageJPG"
  const PACKAGING_IMAGE_JPG = "PackagingImageJPG"
  const PERSPECTIVE_DRAWING_JPG = "PerspectiveDrawingJPG"
  const TOP_VIEW_JPG = "TopViewJPG"
  const FRONT_VIEW_JPG = "FrontViewJPG"
  const SIDE_VIEW_JPG = "SideViewJPG"
  const EXPLODED_DRAWING_JPG = "ExplodedDrawingJPG"
  const OVERVIEW_TABLE_JPG = "OverviewTableJPG"
  const PRIMARY_IMAGE_EPS = "PrimaryImageEPS"
  const MARKETING_IMAGE = "MarketingImage"

  //image types
  const IMAGE_TYPE_PRODUCT = "IMG_TYPE_Product"
  const IMAGE_TYPE_ARTICLE = "IMG_TYPE_Article"

  let mainSortOrder = [IMAGE_TYPE_PRODUCT, IMAGE_TYPE_ARTICLE]
  let secondarySortOrder = [
    PRIMARY_IMAGE_JPG,
    PACKAGING_IMAGE_JPG,
    PERSPECTIVE_DRAWING_JPG,
    TOP_VIEW_JPG,
    FRONT_VIEW_JPG,
    SIDE_VIEW_JPG,
    MARKETING_IMAGE,
  ]
  let excludeList: string[] = []

  if (isArticle) {
    mainSortOrder = [PRIMARY_IMAGE_JPG, PERSPECTIVE_DRAWING_JPG, FRONT_VIEW_JPG, SIDE_VIEW_JPG, TOP_VIEW_JPG]
    secondarySortOrder = [IMAGE_TYPE_ARTICLE, IMAGE_TYPE_PRODUCT]
    excludeList = [PACKAGING_IMAGE_JPG, EXPLODED_DRAWING_JPG, OVERVIEW_TABLE_JPG, PRIMARY_IMAGE_EPS, MARKETING_IMAGE]
  }

  // Filter out all but the first marketing image
  const marketingImage = images.find((img) => img.type === IMAGE_TYPE_PRODUCT && img.orientation === MARKETING_IMAGE)
  images = images.filter((img) => !(img.type === IMAGE_TYPE_PRODUCT && img.orientation === MARKETING_IMAGE))
  marketingImage && images.push(marketingImage)

  // Filter out the images in the exclude list
  images = images.filter((img) => {
    return !excludeList.includes(img.orientation ?? "")
  })

  const sortByType = (a: ProductImageModel, b: ProductImageModel) =>
    mainSortOrder.indexOf(a.type ?? "") - mainSortOrder.indexOf(b.type ?? "")

  const sortByOrientation = (a: ProductImageModel, b: ProductImageModel) =>
    secondarySortOrder.indexOf(a.orientation ?? "") - secondarySortOrder.indexOf(b.orientation ?? "")

  return images.sort(sortByOrientation).sort(sortByType)
}

export function scrollToSection(section: HTMLElement) {
  // this setTimeout is a fix for an issue with the menu not closing when you want to scrollIntoView
  // https://github.com/chakra-ui/chakra-ui/issues/7003

  if (section) {
    setTimeout(() => {
      section.scrollIntoView({
        behavior: "smooth",
      })
    }, 1)
  }
}

// When we have more than 4 entries (length) we want to split them into two columns
// if we have more than 8, then we take the rounded half of the total as rows for the first column
export function determineNumberOfRows(length: number) {
  if (length > sectionColumnBreakpoints.FIRST_BREAKPOINT && length <= sectionColumnBreakpoints.SECOND_BREAKPOINT) {
    return sectionColumnBreakpoints.FIRST_BREAKPOINT
  } else if (length > sectionColumnBreakpoints.SECOND_BREAKPOINT) {
    return Math.round(length / 2)
  } else {
    return length
  }
}

export function getArticleImageData(articlesList: ArticleModel[]) {
  return articlesList.map((article: ArticleModel) => {
    const articleDescription = article?.description?.split(":")[1]
      ? article.description.split(":")[1]
      : article.description
    const articleDisplayName = `${article?.key ?? ""}${articleDescription ? ` (${articleDescription.trim()})` : ""}`

    return {
      name: articleDisplayName || "<no display name>", // If no article key or article description is present
      images: article.gallery.list,
      id: article.key,
    }
  })
}

export const getPriceConfig = (data: GeneralConfigDocumentData): PriceConfigType => {
  return data
    ? {
        hasPrice: data.price ?? false,
        hasNetPrice: data.netprice ?? false,
        hasGrossPrice: data.grossprice ?? false,
        rounding: data.rounding ?? 0,
        thousandSeparator: data.thousand_separator ?? "",
        decimalPoint: data.decimal_point ?? "",
      }
    : {
        hasPrice: false,
        hasNetPrice: false,
        hasGrossPrice: false,
        rounding: 0,
        thousandSeparator: "",
        decimalPoint: "",
      }
}

export const getFurtherProductsCarouselItems = (furtherProductsData: object) => {
  // Max items in the "Further products" carousel
  const FURTHER_PRODUCTS_MAX_ITEMS = 20

  const furtherProductsCampaignItems = getCampaignItems(furtherProductsData, campaignTypes.PDP_FURTHER_PRODUCTS)
  return furtherProductsCampaignItems.slice(0, FURTHER_PRODUCTS_MAX_ITEMS)
}

export const getCombineWithCarouselItems = (combineWithData: object, combineWithProductIDs: string[]) => {
  const combineWithCampaignItems = getCampaignItems(combineWithData, campaignTypes.PDP_COMBINABLE)

  return getSortedItems(combineWithCampaignItems, combineWithProductIDs)
}

export const getAccessoriesCarouselItems = (accessoriesData: object, accessoriesProductIDs: string[]) => {
  const combineWithCampaignItems = getCampaignItems(accessoriesData, campaignTypes.PDP_ACCESSORIES)

  return getSortedItems(combineWithCampaignItems, accessoriesProductIDs)
}

export const getProductAllAdditionalInformation = (additionalInformation: AdditionalInformationListModel) => {
  /* Extract all additional information and flatten data */
  return additionalInformation?.list?.length
    ? additionalInformation.list.reduce((allItems: string[], currentList) => {
        if (!!currentList?.items?.length) allItems = [...allItems, ...currentList.items]
        return allItems
      }, [])
    : []
}
